import { format, formatDistanceToNowStrict, isToday, isTomorrow, isThisWeek, isThisYear, isSameDay, parseISO, differenceInMinutes } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatInTimeZone } from 'date-fns-tz';

type D = string | Date;
function toDate(d: D): Date { return typeof d === 'string' ? parseISO(d) : d; }

export function asDate(d: D | null | undefined): Date | null {
  if (!d) return null;
  return toDate(d);
}

export function formatDayLong(input: D): string {
  const d = toDate(input);
  if (isToday(d)) return "Aujourd'hui";
  if (isTomorrow(d)) return 'Demain';
  return format(d, isThisYear(d) ? 'EEEE d MMMM' : 'EEEE d MMMM yyyy', { locale: fr });
}

export function formatDayShort(input: D): string {
  const d = toDate(input);
  if (isToday(d)) return "AUJOURD'HUI";
  return `${format(d, 'EEE', { locale: fr }).slice(0, 3).toUpperCase()} ${format(d, 'd')} ${format(d, 'MMM', { locale: fr }).toUpperCase().replace('.', '')}`;
}

export function formatDayChip(input: D): string {
  const d = toDate(input);
  if (isToday(d)) return "Aujourd'hui";
  if (isTomorrow(d)) return 'Demain';
  return format(d, 'd MMM', { locale: fr }).replace('.', '');
}

export function formatRangeChip(from: D, to: D): string {
  const f = toDate(from);
  const t = toDate(to);
  if (isSameDay(f, t)) return formatDayChip(f);
  return `${format(f, 'd MMM', { locale: fr }).replace('.', '')} – ${format(t, 'd MMM', { locale: fr }).replace('.', '')}`;
}

/** Format pour la phrase titre : "du 3 mai au 8 mai" */
export function formatRangeProse(from: D, to: D): string {
  const f = toDate(from);
  const t = toDate(to);
  if (isSameDay(f, t)) return `le ${format(f, 'd MMM', { locale: fr }).replace('.', '')}`;
  return `du ${format(f, 'd MMM', { locale: fr }).replace('.', '')} au ${format(t, 'd MMM', { locale: fr }).replace('.', '')}`;
}

export function formatDayNum(input: D): string { return format(toDate(input), 'd'); }
export function formatMonthShort(input: D): string { return format(toDate(input), 'MMM', { locale: fr }).toUpperCase().replace('.', ''); }
export function formatTime(input: D): string {
  return formatInTimeZone(toDate(input), 'Europe/Paris', 'HH:mm');
}

/**
 * Retourne false quand l'événement n'a pas d'heure renseignée (minuit). Les sources
 * encodent « pas d'heure » différemment : DATAtourisme = minuit UTC, Hérault Tourisme /
 * OpenAgenda = minuit Europe/Paris (= 22:00 UTC). On considère donc « sans heure » si
 * l'heure locale Paris OU l'heure UTC vaut 00:00:00.
 */
export function hasKnownTime(input: D): boolean {
  const d = toDate(input);
  if (formatInTimeZone(d, 'Europe/Paris', 'HH:mm:ss') === '00:00:00') return false;
  if (d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0) return false;
  return true;
}

export function formatRange(start: D, end?: D | null): string {
  const s = toDate(start);
  if (!end) return format(s, 'EEE d MMM', { locale: fr }).toUpperCase();
  const e = toDate(end);
  if (format(s, 'yyyy-MM-dd') === format(e, 'yyyy-MM-dd')) {
    return format(s, 'EEE d MMM', { locale: fr }).toUpperCase();
  }
  return `${format(s, 'd MMM', { locale: fr })} → ${format(e, 'd MMM', { locale: fr })}`.toUpperCase();
}

export function isSoon(input: D, withinMin = 60): boolean {
  const diff = differenceInMinutes(toDate(input), new Date());
  return diff >= 0 && diff <= withinMin;
}

export function relativeFromNow(input: D): string {
  return formatDistanceToNowStrict(toDate(input), { locale: fr, addSuffix: true });
}

export { isToday, isTomorrow, isThisWeek };
